import { Inject, Injectable, Injector, Optional, SkipSelf, TemplateRef } from '@angular/core';
import { Location } from '@angular/common';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MAT_DIALOG_DEFAULT_OPTIONS,
  MAT_DIALOG_SCROLL_STRATEGY
} from '@angular/material/dialog';
import { ComponentType, Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { DeleteModalComponent } from '../common-components/delete-modal/delete-modal.component';
import { PopUpComponent } from '../common-components/pop-up/pop-up.component';
import { ReadOnlyService } from './read-only.service';

@Injectable({
  providedIn: 'root'
})
export class AppReadOnlyDialogService extends MatDialog {
  constructor(
    overlay: Overlay,
    injector: Injector,
    @Optional() location: Location,
    @Optional() @Inject(MAT_DIALOG_DEFAULT_OPTIONS) defaultOptions: MatDialogConfig,
    @Inject(MAT_DIALOG_SCROLL_STRATEGY) scrollStrategy: any,
    @Optional() @SkipSelf() parentDialog: MatDialog,
    overlayContainer: OverlayContainer,
    private readOnlyService: ReadOnlyService,
    private toastr: ToastrService
  ) {
    super(overlay, injector, location, defaultOptions, scrollStrategy, parentDialog, overlayContainer);
  }

  open<T, D = any, R = any>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    config?: MatDialogConfig<D>
  ): MatDialogRef<T, R> {
    const isGlobalReadOnly = this.readOnlyService.isReadOnly();

    // Check if component/feature context was passed in config
    const featureKey = (config?.data as any)?.featureKey || (config?.data as any)?.module;
    const isComponentReadOnly = featureKey ? this.readOnlyService.isComponentReadOnly(featureKey) : false;
    const shouldEnforceReadOnly = isGlobalReadOnly || isComponentReadOnly || (config?.data as any)?.read_only === true;

    // If Read-Only mode is active, block pure mutation dialogs (Delete / Reactivate)
    if (shouldEnforceReadOnly) {
      const target: any = componentOrTemplateRef;
      if (
        target === DeleteModalComponent ||
        target === PopUpComponent ||
        target?.name === 'DeleteModalComponent' ||
        target?.name === 'PopUpComponent'
      ) {
        this.toastr.warning('Action not permitted: Application is in Read-Only mode.', 'Read-Only');
        return this.createDummyDialogRef<T, R>();
      }
    }

    // Clone config to inject read-only context safely
    const finalConfig: MatDialogConfig<D> = { ...(config || {}) };

    if (shouldEnforceReadOnly) {
      // Inject read_only flags into dialog data
      const existingData = finalConfig.data || ({} as any);
      finalConfig.data = {
        ...existingData,
        read_only: true,
        isReadOnly: true
      };

      // Append CSS panel class for styling
      const existingPanelClass = finalConfig.panelClass;
      if (Array.isArray(existingPanelClass)) {
        finalConfig.panelClass = [...existingPanelClass, 'app-readonly-dialog', 'app-readonly-scope'];
      } else if (typeof existingPanelClass === 'string') {
        finalConfig.panelClass = [existingPanelClass, 'app-readonly-dialog', 'app-readonly-scope'];
      } else {
        finalConfig.panelClass = ['app-readonly-dialog', 'app-readonly-scope'];
      }
    }

    const dialogRef = super.open(componentOrTemplateRef, finalConfig);

    // When opened in Read-Only mode, sanitize controls and footer buttons
    if (shouldEnforceReadOnly && dialogRef) {
      dialogRef.afterOpened().subscribe(() => {
        try {
          const containerElement = (dialogRef as any)?._containerInstance?._elementRef?.nativeElement as HTMLElement;
          if (containerElement) {
            this.sanitizeDialogDom(containerElement);
          }
        } catch (e) {
          // Graceful fallback
        }
      });
    }

    return dialogRef;
  }

  /**
   * Disables inputs and hides mutating footer buttons inside opened dialog container.
   */
  private sanitizeDialogDom(container: HTMLElement): void {
    if (!container || !container.querySelectorAll) return;

    // 1. Disable inputs, textareas, selects
    const inputs = container.querySelectorAll<HTMLElement>('input, textarea, select');
    inputs.forEach(input => {
      const type = input.getAttribute('type') || '';
      if (type !== 'search') {
        input.setAttribute('readonly', 'true');
        input.classList.add('app-readonly-field');
      }
    });

    // 2. Disable Material form fields
    const matControls = container.querySelectorAll<HTMLElement>(
      'mat-select, mat-checkbox, mat-radio-button, mat-slide-toggle, mat-slider, mat-datepicker-toggle'
    );
    matControls.forEach(ctrl => {
      ctrl.setAttribute('disabled', 'true');
      ctrl.classList.add('mat-form-field-disabled', 'app-readonly-field');
      ctrl.style.pointerEvents = 'none';
    });

    // 3. Footer buttons: Hide Save, Delete, Add, Update, Confirm buttons
    const actionButtons = container.querySelectorAll<HTMLElement>(
      'button[type="submit"], .btn-save, .btn-delete, .btn-add, .btn-update, .btn-confirm, .btn-reactivate'
    );
    actionButtons.forEach(btn => {
      if (
        !btn.hasAttribute('mat-dialog-close') &&
        !btn.classList.contains('btn-cancel') &&
        !btn.classList.contains('btn-close')
      ) {
        btn.style.display = 'none';
        btn.setAttribute('disabled', 'true');
      }
    });
  }

  /**
   * Creates a closed dummy MatDialogRef when a blocked dialog call occurs.
   */
  private createDummyDialogRef<T, R>(): MatDialogRef<T, R> {
    return {
      afterClosed: () => of(undefined as any),
      afterOpened: () => of(undefined as any),
      beforeClosed: () => of(undefined as any),
      backdropClick: () => of(undefined as any),
      keydownEvents: () => of(undefined as any),
      close: () => {},
      getState: () => 2, // CLOSED
      componentInstance: null as any,
      id: 'dummy-read-only-ref',
      addPanelClass: () => {},
      removePanelClass: () => {}
    } as unknown as MatDialogRef<T, R>;
  }
}
